import { View, ActivityIndicator, Pressable, TouchableWithoutFeedback, TouchableOpacity, Text, Platform, DynamicColorIOS } from 'react-native'
import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Redirect, Tabs, useSegments } from 'expo-router';
import { NativeTabs } from 'expo-router/unstable-native-tabs';
import { Badge } from 'react-native-paper';
import { usePathname, useGlobalSearchParams } from 'expo-router';
import { supabase, queryClient } from '@/data/client';
import { ensureFreshSession, restoreSessionWithRetry } from '@/utils/authRefresh';
import { isSignOutInProgress, signOut } from '@/utils/signOut';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { getCachedOrgID, getOrganizationEntitlements, logOutOfOrganization } from '@/data/organization';
import { isFreeOrg, isBillingRestricted, isSetupPending, resolveEffectiveTabs, getDefaultTabKey, applyPlanTransition } from '@/utils/organizationEntitlements';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { BlurView } from 'expo-blur';
import Unicon from '@/components/General/Unicon';
import { useRouter, useNavigation } from 'expo-router';
import * as Sentry from '@sentry/react-native';
import Animated, { useAnimatedStyle, withSpring } from 'react-native-reanimated';
import usePlatform from '@/utils/hooks/usePlatform';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import DrawerMenu from '@/components/General/DrawerMenu';
import useTabConfigStore, { TAB_SLOT_KEYS, DEFAULT_TAB_KEYS } from '@/data/store/useTabConfigStore';
import { getTabOption } from '@/utils/constants/tabOptions';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import NotificationBadge from '@/components/General/NotificationBadge';
import useGuideBlocker from '@/utils/hooks/useGuideBlocker';
import useSlideoutDrawer from '@/utils/hooks/useSlideoutDrawer';
import { formEntryLoginRedirect } from '@/utils/formEntryNotifications';
import followUpTabIcon from '@/assets/follow-up.png';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const isIOS26Plus = Platform.OS === 'ios' && parseInt(Platform.Version, 10) >= 26;

const TabBarButton = ({ children, onPress, onLongPress, accessibilityState, style, isMenuTab: _isMenuTab, ...rest }) => {
    const focused = accessibilityState?.selected;
    const scale = useAnimatedStyle(() => ({
        transform: [{ scale: withSpring(focused ? 1 : 0.95, { damping: 15, stiffness: 200 }) }],
    }));

    return (
        <AnimatedPressable
            onPress={onPress}
            onLongPress={onLongPress}
            hitSlop={10}
            style={[styles.tabButton, style, scale]}
            android_ripple={{ color: 'transparent' }}
            {...rest}
        >
            {children}
        </AnimatedPressable>
    );
};

export default function Layout() {
    if (__DEV__) { (globalThis.__renderCounts ||= {}).tabsLayout = ((globalThis.__renderCounts.tabsLayout || 0) + 1); }
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    // null | 'offline' | 'retrying' — shown under the auth spinner while the
    // session restore is waiting on a bad network (never falls to <Redirect>).
    const [authRetrying, setAuthRetrying] = useState(null);
    const [isRetrySigningOut, setIsRetrySigningOut] = useState(false);
    const [billingBlocked, setBillingBlocked] = useState(false);
    const drawer = useSlideoutDrawer();
    useGuideBlocker(drawer.open);
    const { theme: { scheme } } = useUnistyles();

    const pathname = usePathname();
    const { file: requestedEntryFile } = useGlobalSearchParams();
    const segments = useSegments();
    const {isLandscape, isTablet} = usePlatform();
    const router = useRouter();
    const insets = useSafeAreaInsets();

    const initializedTabs = useRef({ '(home)': true });
    const checkAuthCancelledRef = useRef(false);
    const customTabs = useTabConfigStore((state) => state.customTabs);
    const hydrateTabConfig = useTabConfigStore((state) => state.hydrate);
    const setTabPlanContext = useTabConfigStore((state) => state.setPlanContext);
    const { entitlements } = useOrganizationEntitlements();

    // Effective per-org tabs: Free orgs sanitize the stored selection against
    // the plan-allowed set without touching @tabConfig:v1, so a re-upgrade
    // restores the user's Paid choices.
    const resolvedTabs = useMemo(
        () => resolveEffectiveTabs(customTabs, entitlements, DEFAULT_TAB_KEYS),
        [customTabs, entitlements]
    );
    // Stable identity: entitlements refetches return a fresh object each time
    // even when the payload is identical — holding the previous reference when
    // the resolved slot map is unchanged keeps navigator children from being
    // rebuilt (a rebuild unmounts the whole tab group).
    const effectiveTabsRef = useRef(resolvedTabs);
    if (JSON.stringify(effectiveTabsRef.current) !== JSON.stringify(resolvedTabs)) {
        effectiveTabsRef.current = resolvedTabs;
    }
    const effectiveTabs = effectiveTabsRef.current;

    const getSlotOption = (tabName) => {
        const groupTypes = isFreeOrg(entitlements) ? [] : smallGroupTypes;
        const viewKey = effectiveTabs?.[tabName] || DEFAULT_TAB_KEYS[tabName];
        const option = getTabOption(viewKey, groupTypes, bibleStudiesContext);
        if (option.permissionCheck?.(loggedInUser, entitlements, bibleStudiesContext) !== true) {
            return getTabOption(getDefaultTabKey(tabName, entitlements, DEFAULT_TAB_KEYS), groupTypes, bibleStudiesContext);
        }
        return option;
    };

    const getTabInitialRoute = (tabName) => {
        const option = getSlotOption(tabName);
        return `/(tabs)/${tabName}${option.route}`;
    };

    useEffect(() => {
        hydrateTabConfig();
    }, []);

    useEffect(() => {
        // reset initialized state so the next tab press navigates to the new initial route
        initializedTabs.current = { '(home)': true };
    }, [customTabs]);

    const { data: loggedInUser } = Query.people.loggedInUser()
    const isFree = isFreeOrg(entitlements);
    const { data: registrations } = Query.registrations.all({options: {enabled: loggedInUser?.isAdmin}});
    // Small group types only feed Paid tabs/drawer items — never fetch for Free.
    const { data: smallGroupTypes } = Query.smallGroupTypes.all({options: {enabled: !isFree}});
    // Org row only feeds the Bible Studies tab gate — cached elsewhere, cheap.
    const { data: organization } = Query.organization.byId({
        id: loggedInUser?.organizationID,
        options: { enabled: !isFree && !!loggedInUser?.organizationID }
    });
    const bibleStudyType = useMemo(
        () => (smallGroupTypes || []).find(t => t.purpose === 'BIBLE_STUDY' && t.roleID) || null,
        [smallGroupTypes]
    );
    const bibleStudiesContext = useMemo(() => ({
        enableBibleStudies: organization?.enableBibleStudies === true,
        bibleStudyType,
    }), [organization?.enableBibleStudies, bibleStudyType]);

    // Keep the tab-config store pointed at the plan-appropriate storage slot
    // (Free writes must never overwrite stored Paid selections).
    useEffect(() => {
        setTabPlanContext(isFree);
    }, [isFree]);

    const { totalUnread: notificationCount } = useNotificationBadges();
    const registrationCount = Number(registrations?.length || 0)

    // TEMP DEBUG: catches the remount driver — mount count staying 1 while
    // children remount = navigator recreating the group; count incrementing =
    // the whole tabs layout remounts.
    const tabsMount = useRef(0);
    const gateState = useRef(null);
    useEffect(() => {
        tabsMount.current++;
        if (__DEV__) console.log(`[tabs-mount] mounted #${tabsMount.current}`);
        return () => { if (__DEV__) console.log(`[tabs-mount] unmounted #${tabsMount.current}`); };
    }, []);
    // TEMP DEBUG: logs every nav-state change — a repeating action here is the
    // ~1Hz driver tearing down the (tabs) group.
    const nav = useNavigation();
    useEffect(() => {
        if (!__DEV__) return;
        const unsub = nav.addListener('state', (e) => {
            console.log(`[nav-state] ${JSON.stringify(e.data.state?.routes?.map(r => r.name))}`);
        });
        return unsub;
    }, [nav]);
    if (__DEV__) {
        const state = `${isCheckingAuth}|${isAuthenticated}|${billingBlocked}|${isFree}|${!!entitlements}`;
        if (gateState.current !== state) {
            console.log(`[tabs-gate] checking=${isCheckingAuth} authed=${isAuthenticated} billing=${billingBlocked} free=${isFree} ent=${!!entitlements}`);
            gateState.current = state;
        }
    }

    const checkAuth = async () => {
        // Resilient restore: getSession() resolves { session: null, error } on
        // transient network failure WITHOUT removing the persisted session —
        // treating that as logged out kicked users to /auth/login on slow
        // networks. This retries transient errors (NetInfo-gated, backoff) and
        // only returns null for a genuinely absent/invalid session.
        const session = await restoreSessionWithRetry({
            onRetry: (_attempt, reason) => {
                if (!checkAuthCancelledRef.current) setAuthRetrying(reason);
            },
            shouldAbort: () => checkAuthCancelledRef.current || isSignOutInProgress(),
        });
        if (checkAuthCancelledRef.current) return;
        setAuthRetrying(null);
        const orgID = session?.user?.app_metadata?.organizationID;

        if(session?.user?.id && orgID){
            // Plan gate on cold start — resolve authoritative entitlements for
            // the persisted org. Fail closed on entry, but only sever the org
            // membership when the RPC positively reports a blocking state
            // (non-membership, or billing_restricted for a member who can't
            // fix it). A transient fetch error must NOT wipe organizationID
            // from auth metadata — that would permanently kick the user to
            // org selection for a network blip.
            let entryEntitlements = null;
            try {
                entryEntitlements = await queryClient.fetchQuery({
                    queryKey: QueryKeys.organizationEntitlements.current(getCachedOrgID()),
                    queryFn: getOrganizationEntitlements,
                    // Cold-start gate must evaluate a live plan — bypass any
                    // hydrated/primed copy that still falls inside staleTime.
                    staleTime: 0,
                });
            } catch (error) {
                Sentry.logger.warn("Entitlements check failed on cold start, blocking entry", { organization_id: orgID, error });
            }

            // billing_restricted and setup_pending both block members; only
            // admins/owners get in to run the billing recovery path.
            const restricted = isBillingRestricted(entryEntitlements) || isSetupPending(entryEntitlements);
            const canFixBilling = entryEntitlements?.isAdmin === true || entryEntitlements?.isAccountOwner === true;
            const entryAllowed = entryEntitlements?.member === true && (!restricted || canFixBilling);

            if(!entryAllowed){
                Sentry.logger.info("Organization blocked by access state on cold start", { organization_id: orgID, accessState: entryEntitlements?.accessState });
                if(entryEntitlements != null){
                    // Positive denial — clear the org claim. Restricted members
                    // land back on the picker (card shows the billing issue);
                    // leaving the claim would bounce them between layouts.
                    await logOutOfOrganization();
                }
                setIsAuthenticated(false);
                setBillingBlocked(true);
                setIsCheckingAuth(false);
                return;
            }

            Sentry.logger.info("User authenticated for main app", { organization_id: orgID });
            setIsAuthenticated(true);
            setIsCheckingAuth(false);

            // Refresh permissions after auth check so it doesn't block initial render
            try {
                await supabase.rpc('refresh_user_permissions');
                // Only force a token refresh when the current JWT has plenty of
                // life left (>5 min). Near expiry, autoRefreshToken handles it
                // within ~60s — a concurrent refreshSession() races on the same
                // refresh token and Supabase's reuse detection revokes the
                // session family → random logouts.
                const session = (await supabase.auth.getSession()).data?.session;
                const secondsLeft = session?.expires_at ? session.expires_at - Date.now() / 1000 : 0;
                if (secondsLeft > 300) {
                    await ensureFreshSession();
                }
            } catch (error) {
                console.warn('Failed to refresh permissions on startup:', error);
            }
        }
        else{
            setIsAuthenticated(false);
            setIsCheckingAuth(false);
        }
    }

    useEffect(() => {
        checkAuthCancelledRef.current = false;
        checkAuth();

        return () => {
            checkAuthCancelledRef.current = true;
        };
    }, []);

    // Org/planRevision change → cancel in-flight work, drop paid-module caches
    // for Free, and rebuild the logged-in user's permission functions.
    useEffect(() => {
        if (entitlements?.member === true) {
            applyPlanTransition(queryClient, entitlements);
        }
    }, [entitlements]);

    // React to sign-out so the <Redirect> below fires without needing a remount.
    // checkAuth() only runs on mount, so without this the tabs layout keeps
    // isAuthenticated=true after supabase.auth.signOut() and never redirects.
    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
            if (event === 'SIGNED_OUT') {
                setIsAuthenticated(false);
                setIsCheckingAuth(false);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Shared tab-press listener factory — pops to initial route on re-tap.
    // Assistant is a modal action: keep the current tab stack underneath it.
    const makeTabListener = (tabName) => ({ navigation }) => ({
        tabPress: (event) => {
            const option = getSlotOption(tabName);
            if (option.key === 'assistant') {
                event?.preventDefault?.();
                const currentTab = segments[0] === '(tabs)' && segments[1] ? segments[1] : '(home)';
                router.push(`/(tabs)/${currentTab}/assistant`);
                return;
            }

            const route = getTabInitialRoute(tabName);
            if (!initializedTabs.current[tabName]) {
                initializedTabs.current[tabName] = true;
                router.replace(route);
            } else if (navigation.isFocused()) {
                router.navigate(route);
            }
        },
    });

    // Retained after first open: the panel/menu stay mounted off-screen (slide
    // value 0) instead of remounting per open. pointerEvents + the a11y hides
    // keep the hidden panel inert — see useSlideoutDrawer for the lifecycle.
    const drawerOverlay = drawer.mounted && (
        <>
            <TouchableWithoutFeedback onPress={drawer.closeDrawer} accessible={drawer.open}>
                <Animated.View
                    style={[styles.drawerBackdrop, drawer.backdropStyle]}
                    pointerEvents={drawer.open ? 'auto' : 'none'}
                    accessibilityElementsHidden={!drawer.open}
                    importantForAccessibility={drawer.open ? 'auto' : 'no-hide-descendants'}
                />
            </TouchableWithoutFeedback>
            <Animated.View
                style={[styles.drawerPanel, drawer.panelStyle]}
                pointerEvents={drawer.open ? 'auto' : 'none'}
                accessibilityElementsHidden={!drawer.open}
                importantForAccessibility={drawer.open ? 'auto' : 'no-hide-descendants'}
                onLayout={drawer.onPanelLayout}
            >
                <DrawerMenu onClose={drawer.closeDrawer} asSlideout isVisible={drawer.open} />
            </Animated.View>
        </>
    );

    if (isCheckingAuth) {
        return (
            <View style={{flex:1, justifyContent:"center", alignItems:"center"}}>
                <ActivityIndicator size="large" />
                {authRetrying && (
                    <View style={styles.authRetryBox}>
                        <Text style={styles.authRetryText}>
                            {authRetrying === 'offline'
                                ? 'No internet connection — waiting to reconnect…'
                                : 'Your connection seems slow — still trying…'}
                        </Text>
                        <TouchableOpacity
                            testID="tabs-auth-retry-sign-out"
                            accessibilityRole="button"
                            accessibilityLabel="Sign out"
                            activeOpacity={0.7}
                            disabled={isRetrySigningOut}
                            onPress={async () => {
                                if (isRetrySigningOut || isSignOutInProgress()) return;
                                setIsRetrySigningOut(true);
                                try {
                                    await signOut('local', 'tabs-slow-network-user-requested');
                                } catch (error) {
                                    Sentry.logger.warn('Tabs auth retry sign out failed', { error });
                                } finally {
                                    setIsRetrySigningOut(false);
                                }
                            }}
                            style={styles.authRetrySignOut(isRetrySigningOut)}
                        >
                            <Text style={styles.authRetrySignOutText}>
                                {isRetrySigningOut ? 'Signing Out...' : 'Sign Out'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                )}
            </View>
        );
    }

    if (!isAuthenticated) {
        if (billingBlocked) {
            return <Redirect href="/auth/select-organization" />;
        }
        return <Redirect href={{pathname: "/auth/login", params: {redirectTo: formEntryLoginRedirect(pathname, requestedEntryFile)}}} />;
    }

    // iOS 26+: NativeTabs with Liquid Glass. Menu is the first tab (disabled, toggles drawer).
    // iPad renders native tabs at the top and pushes the stack header down, so use JS tabs there.
    if (isIOS26Plus && !isTablet) {
        const tabTintColor = DynamicColorIOS({ dark: '#7B9BFF', light: '#4361EE' });
        const tabIconColor = DynamicColorIOS({ dark: '#949eb1', light: '#6B7280' });

        return (
            <View style={{flex: 1}}>
                <NativeTabs
                    initialRouteName="(home)"
                    hidden={isLandscape}
                    tintColor={tabTintColor}
                    iconColor={{ default: tabIconColor, selected: tabTintColor }}
                >
                    {/* Menu — first tab, disabled so it doesn't navigate. tabPress toggles drawer. */}
                    <NativeTabs.Trigger
                        name="menu"
                        disabled
                        role='search'
                        listeners={{
                            tabPress: () => {
                                drawer.open ? drawer.closeDrawer() : drawer.openDrawer();
                            },
                        }}
                    >
                        <NativeTabs.Trigger.Icon
                            sf={{ default: 'line.3.horizontal', selected: 'line.3.horizontal' }}
                        />
                    </NativeTabs.Trigger>

                    {TAB_SLOT_KEYS.map((slot) => {
                        const option = getSlotOption(slot);
                        return (
                            <NativeTabs.Trigger
                                key={slot}
                                name={slot}
                                testID={option.key === 'assistant' ? 'assistant-tab' : undefined}
                                listeners={makeTabListener(slot)}
                            >
                                {option.key === 'followUp' ? (
                                    <NativeTabs.Trigger.Icon src={followUpTabIcon} renderingMode="template" />
                                ) : (
                                    <NativeTabs.Trigger.Icon
                                        sf={{ default: option.sfDefault, selected: option.sfSelected }}
                                    />
                                )}
                                <NativeTabs.Trigger.Label>{option.shortLabel || option.label}</NativeTabs.Trigger.Label>
                                {option.key === 'notifications' && notificationCount > 0 && (
                                    <NativeTabs.Trigger.Badge>
                                        {notificationCount > 99 ? '99+' : String(notificationCount)}
                                    </NativeTabs.Trigger.Badge>
                                )}
                            </NativeTabs.Trigger>
                        );
                    })}
                </NativeTabs>

                {drawerOverlay}
            </View>
        );
    }

    // iOS <26, Android, web: existing custom tab bar
    return (
        <View style={{flex: 1}}>
        <Tabs
            initialRouteName="(home)"
            screenOptions={({ route }) => ({
                unmountOnBlur: false,
                // Freeze inactive tab scenes instead of unmounting — retained
                // screens stop re-rendering until the tab regains focus.
                freezeOnBlur: true,
                headerShown:false,
                tabBarStyle: {
                    height: 80 + (Platform.OS === 'android' ? (insets.bottom || 0) : 0),
                    paddingBottom: Platform.OS === 'android' ? (insets.bottom || 0) : 0,
                    backgroundColor: scheme === 'dark'
                        ? 'rgba(28, 32, 36, 0.85)'
                        : 'rgba(255, 255, 255, 0.85)',
                    borderTopWidth: 1,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: scheme === 'dark' ? 0.3 : 0.12,
                    shadowRadius: 16,
                    elevation: 12,
                    paddingHorizontal: 8,
                    display:isLandscape ? 'none' : 'flex',
                },
                tabBarItemStyle: {
                    paddingVertical: 8,
                },
                transitionDuration: 100,
                transitionSpec: {
                    animation: 'timing',
                    config: {
                        duration: 100,
                    },
                },
                tabBarBackground: () => (
                    <BlurView tint={scheme} intensity={80} style={styles.tabBarBackground} />
                ),
                sceneStyleInterpolator: ({ current }) => ({
                    sceneStyle: {
                        opacity: current.progress.interpolate({
                            inputRange: [-1, 0, 1],
                            outputRange: [0, 1, 0],
                        }),
                    },
                }),
                tabBarLabelStyle: styles.tabBarLabel,
                tabBarButton: (props) => (
                    <TabBarButton {...props} isMenuTab={route.name === 'menu'} />
                ),
                tabBarIcon: ({ focused, size }) => {
                    const option = getSlotOption(route.name);
                    const iconName = focused ? option.icon : option.iconOutline;
                    const library = option.iconLib;
                    size = 26;

                    if (route.name === 'menu') {
                        return (
                            <View style={styles.menuIconContainer}>
                                <View style={styles.menuIconPill}>
                                    <Unicon
                                        testID="menu"
                                        name={drawer.open ? "menu-open" : "menu-close"}
                                        size={22}
                                        library="material"
                                        colorVariant="onPrimary"
                                    />
                                </View>
                                {registrationCount > 0 && (
                                    <Badge size={20} style={styles.badge}>
                                        {registrationCount > 99 ? '99+' : registrationCount}
                                    </Badge>
                                )}
                            </View>
                        );
                    }

                    return (
                        <View style={styles.iconContainer}>
                            <View style={styles.iconWrapper(focused)}>
                                <Unicon
                                    testID={option.key === 'assistant' ? 'assistant-tab' : route.name}
                                    name={iconName}
                                    size={size}
                                    library={library}
                                    colorVariant={focused ? "primary" : "textSecondary"}
                                />
                            </View>
                            {option.key === 'notifications' && (
                                <NotificationBadge count={notificationCount} size={18} style={styles.badge} testID="tab-badge-notifications" />
                            )}
                        </View>
                    );
                }
            })}
        >
            <Tabs.Screen
                name="menu"
                options={{
                    title: "",
                }}
                listeners={{
                    tabPress: (e) => {
                        e.preventDefault();
                        drawer.open ? drawer.closeDrawer() : drawer.openDrawer();
                    },
                }}
            />

            {TAB_SLOT_KEYS.map((slot) => (
                <Tabs.Screen
                    key={slot}
                    name={slot}
                    options={{
                        headerShown: false,
                    }}
                    listeners={makeTabListener(slot)}
                />
            ))}

        </Tabs>

        {drawerOverlay}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    authRetryBox: {
        marginTop: theme.spacing.xl,
        alignItems: 'center',
        paddingHorizontal: theme.spacing.lg,
    },
    authRetryText: {
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
        textAlign: 'center',
    },
    authRetrySignOut: (disabled) => ({
        minHeight: 44,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        marginTop: theme.spacing.md,
        opacity: disabled ? 0.5 : 1,
    }),
    authRetrySignOutText: {
        color: theme.colors.error,
        fontFamily: theme.fonts.bold,
    },
    tabBarBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 32,
        overflow: 'hidden',
    },
    tabBarLabel: {
        display: 'none',
    },
    tabButton: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: 56,
        minHeight: 48,
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    iconContainer: {
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
        width: 45,
        height: 45,
    },
    iconWrapper: (focused) => ({
        justifyContent: 'center',
        alignItems: 'center',
        width: 45,
        height: 45,
        borderRadius: 10,
        backgroundColor: focused
            ? theme.colors.bgPrimary
            : 'transparent',
    }),
    badge: {
        position: 'absolute',
        top: -2,
        right: 0,
        fontSize: 12,
        fontFamily: theme.fonts.bold,
    },
    menuIconContainer: {
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
        width: 45,
        height: 45,
    },
    menuIconPill: {
        justifyContent: 'center',
        alignItems: 'center',
        width: 42,
        height: 42,
        borderRadius: 42,
        backgroundColor: theme.colors.primary,
        shadowColor: theme.colors.primary,
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.4,
        shadowRadius: 6,
        elevation: 6,
    },
    drawerBackdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        zIndex: 100,
    },
    drawerPanel: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        width: 300,
        zIndex: 101,
        shadowColor: '#000',
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 0.25,
        shadowRadius: 16,
        elevation: 20,
    },
}))
